<template>
  <l-map
    id="map"
    ref="map"
    :zoom="+mapZoom"
    :min-zoom="minZoom"
    :max-zoom="maxZoom"
    :max-bounds="[NWcoords, SEcoords]"
    :options="{ zoomControl: false }"
    :center="center"
    @update:zoom="zoomUpdated"
  >
    <MapLegends position="bottomleft" />
    <l-control-zoom position="bottomleft" />
    <BasemapLayer />
    <LocationMarker />
    <GeojsonLayer
      ref="geojsonLayer"
      @update-selected-geometry="$emit('update:selectedGeometry', $event)"
      @update-selected-geometry-name="$emit('update:selectedGeometryName', $event)"
    />
    <MapBoundingBox v-if="showBounds" />
  </l-map>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'

import { Map, LatLngBounds } from 'leaflet'
import { LMap, LControlZoom } from 'vue2-leaflet'
import MapLegends from '@/components/public/Visualisations/TheMap/MapLegends.vue'
import MapBoundingBox from '@/components/public/Visualisations/TheMap/MapBoundingBox.vue'
import BasemapLayer from '@/components/public/Visualisations/TheMap/BasemapLayer.vue'
import LocationMarker from '@/components/public/Visualisations/TheMap/LocationMarker.vue'
import GeojsonLayer from '@/components/public/Visualisations/TheMap/GeojsonLayer.vue'
import { MAP_CONFIG_KEY } from '@/constants'

const components = {
  LMap,
  LControlZoom,
  MapLegends,
  MapBoundingBox,
  LocationMarker,
  BasemapLayer,
  GeojsonLayer,
}

const props = {
  showBounds: {
    type: Boolean,
    default: false,
  },
  anyZoom: Boolean,
  selectedGeometry: {
    type: String,
    default: null,
  },
  selectedGeometryName: {
    type: String,
    default: '',
  },
}

interface Refs extends Vue {
  geojsonLayer: {
    getBounds: LatLngBounds
  }
  map: {
    mapObject: Map
  }
}

// eslint-disable-next-line no-use-before-define
@Component<TheMap>({
  components,
  watch: {
    loadingMapData(loading) {
      if (!loading) this.$nextTick(() => this.onLoadedMapData())
    },
  },
})
export default class TheMap extends Vue.extend({ props }) {
  map!: Map

  mounted() {
    this.$typedStore.public.geometries.update()
    this.$nextTick(() => {
      this.map = (this.$refs as unknown as Refs).map.mapObject
    })
  }

  get mapZoom() {
    return this.$typedStore.public.display.mapZoom
  }

  set mapZoom(value) {
    this.$typedStore.public.display.setMapZoom(value)
  }

  get SEcoords() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config[MAP_CONFIG_KEY].extent
      .SEcoords
  }

  get NWcoords() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config[MAP_CONFIG_KEY].extent
      .NWcoords
  }

  get SEcoordsLat() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config[MAP_CONFIG_KEY].extent
      .SEcoords.lat
  }

  get SEcoordsLng() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config[MAP_CONFIG_KEY].extent
      .SEcoords.lng
  }

  get NWcoordsLat() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config[MAP_CONFIG_KEY].extent
      .NWcoords.lat
  }

  get NWcoordsLng() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config[MAP_CONFIG_KEY].extent
      .NWcoords.lng
  }

  get loadingMapData() {
    return !this.$typedStore.public.geometries.loaded
  }

  get maxZoom() {
    return this.anyZoom
      ? 14
      : Number(
          this.$typedStore.activeVisualisation.visualisationStore.app.config.mapConfig.maxScale,
        )
  }

  get minZoom() {
    return this.anyZoom
      ? 0
      : Number(
          this.$typedStore.activeVisualisation.visualisationStore.app.config.mapConfig.minScale,
        )
  }

  get center() {
    const lat = (Number(this.NWcoordsLat) + Number(this.SEcoordsLat)) / 2
    const lng = (Number(this.NWcoordsLng) + Number(this.SEcoordsLng)) / 2
    return [lat, lng]
  }

  zoomUpdated(zoom: number) {
    this.mapZoom = zoom
  }

  onLoadedMapData() {
    // getBounds is not a function because it's refering to GeojsonLayer component getBound getter
    const bounds = (this.$refs as unknown as Refs).geojsonLayer.getBounds
    if (bounds && bounds.isValid() && this.map) this.map.fitBounds(bounds)
  }
}
</script>

<style scoped>
#map {
  width: 100%;
  height: 100%;
}
</style>

<style>
.map-marker-override {
  width: 100% !important;
  height: 100% !important;
}

.leaflet-bottom.leaflet-left {
  /* Active tab is 15px pixel longer,
  and leaflet existing margin is 10px */
  margin-left: 5px;
}
</style>
